import { Group, Menu, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconDotsVertical, IconDownload, IconTrashX } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import FileDownloadLinkModal from 'components/tables/data/system-export/FileDownloadLinkModal';
import { useConfirm } from 'components/modals/message/MessageProvider';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import { noop } from 'lodash';
import { useCallback } from 'react';
import panic from 'errors/panic';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';

interface SystemExportActionsProps {
  systemExportId: number;
  fileId: string;
  compactOnTablet?: boolean;
  onDelete?: () => void;
}

/**
 * Actions for the system export table.
 */
export default function SystemExportActions({
  systemExportId,
  fileId,
  compactOnTablet = false,
  onDelete = noop,
}: SystemExportActionsProps) {
  const { getAction } = useApi();
  const { confirm } = useConfirm();
  const { isMobile, tableActionMode } = useResponsiveDesign();
  const isCompact = compactOnTablet && tableActionMode !== 'desktop';
  const menuOnly = compactOnTablet && tableActionMode === 'mobile';
  const [linkModalOpened, { open: openLinkModal, close: closeLinkModal }] = useDisclosure(false);

  const confirmDelete = useCallback(() => {
    const deleteSystemExport = getAction('SystemExportDelete');

    confirm({
      title: 'Zmazať export',
      content: 'Naozaj chcete zmazať tento export? Táto akcia je nevratná.',
      onConfirm: () =>
        deleteSystemExport({ parameters: { systemExportId: String(systemExportId) } })
          .then(onDelete)
          .catch(panic),
    });
  }, [confirm, getAction, systemExportId, onDelete]);

  return (
    <Group spacing={12} noWrap>
      {!menuOnly &&
        (isCompact ? (
          <Tooltip label="Stiahnuť" events={{ hover: true, focus: true, touch: true }}>
            <ActionIcon variant="secondary" size="md" onClick={openLinkModal}>
              <IconDownload />
            </ActionIcon>
          </Tooltip>
        ) : (
          <Button size="md" variant="secondary" leftIcon={<IconDownload />} onClick={openLinkModal}>
            Stiahnuť
          </Button>
        ))}
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon variant="tertiary" size={isMobile ? 'sm' : 'md'}>
            <IconDotsVertical stroke="1.5" />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          {menuOnly && (
            <>
              <Menu.Item icon={<IconDownload stroke={1.5} size={20} />} onClick={openLinkModal}>
                Stiahnuť
              </Menu.Item>
              <Menu.Divider />
            </>
          )}
          <Menu.Item color="red.8" icon={<IconTrashX stroke={1.5} size={20} />} onClick={confirmDelete}>
            Zmazať
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      <FileDownloadLinkModal fileId={fileId} opened={linkModalOpened} onClose={closeLinkModal} />
    </Group>
  );
}
